"use client";
import styles from "./index.module.scss";
import React, { useEffect, useState } from "react";
import { useTranslations, useCurrentLocale } from "@/app/lib/LocaleProvider";
import { useProtocolStore } from "@/app/store/robot/protocolSlice";
import useReportRequestQueue from "@/app/hooks/robot/useReportRequestQueue";
import {
  ReportBizTypeEnum,
  ReportEventEnum,
  ReportSceneTypeEnum,
} from "@/app/types/typeConfig/report";
import { isMobileDevice, isHandyApp } from "@/app/utils/device";

const FALLBACK_LOCALE = "en-US";

export default function AIProtocolModal() {
  const [bambuAIAccept, setBambuAIAccept] = useState(true);
  const t = useTranslations();
  const locale = useCurrentLocale();
  const { addToReportQueue } = useReportRequestQueue();
  const storeBambuAIAccept = useProtocolStore((s) => s.bambuAIAccept);

  useEffect(() => {
    const storageHasAccept: string =
      typeof window !== "undefined"
        ? localStorage.getItem("bambu-ai-accept") || "0"
        : "0";
    const storageAcceptFlag = Boolean(
      storeBambuAIAccept === "1" || storageHasAccept === "1",
    );
    setBambuAIAccept(storageAcceptFlag);
    if (!storageAcceptFlag) {
      addToReportQueue({
        bizType: ReportBizTypeEnum.PRIVACY,
        createTime: Date.now(),
        eventType: ReportEventEnum.SHOW,
        scene: ReportSceneTypeEnum.NORMAL_CONSULT,
        cacheKey: `${isMobileDevice() ? "mobile" : "pc"}_scene${ReportSceneTypeEnum.NORMAL_CONSULT}${isHandyApp() ? "_handy_app" : ""}`,
      });
    }
  }, [addToReportQueue, storeBambuAIAccept]);

  const handleOK = () => {
    setBambuAIAccept(true);
    if (typeof window !== "undefined") {
      localStorage.setItem("bambu-ai-accept", "1");
    }
    addToReportQueue({
      bizType: ReportBizTypeEnum.PRIVACY,
      createTime: Date.now(),
      eventType: ReportEventEnum.CLICK,
      scene: ReportSceneTypeEnum.NORMAL_CONSULT,
    });
  };

  if (bambuAIAccept) return null;

  const isEn = locale === FALLBACK_LOCALE;
  const policiesTermsUrl = isEn
    ? "https://bambulab.com/en/policies/terms"
    : "https://bambulab.cn/zh-cn/policies/terms";
  const policiesPrivacyUrl = isEn
    ? "https://bambulab.com/en/policies/privacy"
    : "https://bambulab.cn/zh-cn/policies/privacy";

  const aiModal = t.aiProtocolModal as {
    title: string;
    protocolContent: string;
    protocolContent1: string;
    protocolContent2: string;
    continue: string;
    tips: string;
  };

  const renderProtocolContent1 = () => {
    const s = aiModal.protocolContent1;
    const regex = /<(1|3)>([\s\S]*?)<\/\1>/g;
    const parts: (string | React.ReactElement)[] = [];
    let lastIndex = 0;
    let key = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(s)) !== null) {
      if (m.index > lastIndex) parts.push(s.slice(lastIndex, m.index));
      const url = m[1] === "1" ? policiesTermsUrl : policiesPrivacyUrl;
      parts.push(
        <a key={key++} href={url} rel="noreferrer">
          {m[2]}
        </a>,
      );
      lastIndex = m.index + m[0].length;
    }
    if (parts.length === 0) return s;
    if (lastIndex < s.length) parts.push(s.slice(lastIndex));
    return parts;
  };

  return (
    <>
      <div className={styles.protocolBoxModal} />
      <div className={styles.protocolContentBox}>
        <div className={styles.title}>{aiModal.title}</div>
        <div className={styles.contentWrapper}>
          <div className={styles.protocolContent}>
            {aiModal.protocolContent}
          </div>
          <div className={styles.protocolContent}>
            {renderProtocolContent1()}
          </div>
          <div className={styles.protocolContent}>
            {aiModal.protocolContent2}
          </div>
        </div>
        <div className={styles.btnWrapper}>
          <div className={styles.continue} onClick={handleOK} role="button">
            {aiModal.continue}
          </div>
          <div className={styles.desc}>{aiModal.tips}</div>
        </div>
      </div>
    </>
  );
}
